import { useState } from 'react';
import { Calendar, type DateRange } from '../Calendar';
import { Popover } from '../Popover';
import { TextField, type TextInputSize } from '../TextField';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarRange, faCalendar } from '@fortawesome/pro-light-svg-icons';
import { Button } from '../Button';
import { t } from 'i18next';

type BaseProps = {
  label?: string;
  required?: boolean;
  helperText?: string;
  className?: string;
  errorMessage?: string;
  disabled?: boolean;
  showErrorBorder?: boolean;
  size?: TextInputSize;
  autoComplete?: string;
};

type PropsSingle = {
  mode: 'single';
  selected?: Date;
  onSelect?: (selected?: Date) => void;
  showIcon?: boolean;
};

type PropsRange = {
  mode: 'range';
  selected?: DateRange;
  onSelect?: (selected?: DateRange) => void;
  showIcon?: never;
};

export type CalendarInputProps = BaseProps & (PropsSingle | PropsRange);

// If start/end months change required, also proper mask adjustment should be implemented.
const START_MONTH = new Date('2000-01-01');
const END_MONTH = new Date('2099-12-31');

export const CalendarInput = ({ mode, selected, onSelect, showIcon, ...labelProps }: CalendarInputProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger
        onClick={(e) => {
          isOpen && e.preventDefault();
        }}
      >
        {mode === 'single' ? (
          <TextField
            enableClearButton
            type="date"
            value={selected}
            StartIcon={showIcon ? () => <FontAwesomeIcon className="text-icon-primary" icon={faCalendar} /> : undefined}
            onChange={(value) => onSelect?.(value)}
            onFocus={() => setIsOpen(true)}
            {...labelProps}
          />
        ) : (
          <TextField
            enableClearButton
            StartIcon={() => <FontAwesomeIcon className="text-icon-primary" icon={faCalendarRange} />}
            type="dateRange"
            value={selected}
            onChange={(value) => onSelect?.(value)}
            onFocus={() => setIsOpen(true)}
            {...labelProps}
          />
        )}
      </Popover.Trigger>
      <Popover.PureContent
        align="start"
        className="z-5202"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (event.target instanceof Element && event.target.closest('input')) {
            event.preventDefault();
          }
        }}
      >
        <div className="p-3">
          {mode === 'single' ? (
            <Calendar
              captionLayout="dropdown"
              endMonth={END_MONTH}
              mode="single"
              month={selected}
              selected={selected}
              startMonth={START_MONTH}
              onMonthChange={onSelect}
              onSelect={onSelect}
            />
          ) : (
            <Calendar
              resetOnSelect
              captionLayout="dropdown"
              endMonth={END_MONTH}
              mode="range"
              numberOfMonths={2}
              selected={selected}
              startMonth={START_MONTH}
              onSelect={onSelect}
            />
          )}
        </div>
        <div className="flex border-t border-border-primary p-3">
          <Button className="ml-auto" onClick={() => setIsOpen(false)}>
            {t('common.close')}
          </Button>
        </div>
      </Popover.PureContent>
    </Popover.Root>
  );
};
